import { Button } from '@/components/UI/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPrimaryAction,
  DialogSecondaryAction,
  DialogTitle
} from '@/components/UI/Dialog';
import { Typography } from '@/components/UI/Typography';
import { useResetPlaygroundDemoDataMutation } from '@/util/hooks/mutations/usePlaygroundDemoDataMutation';
import usePlaygroundMode from '@/util/hooks/queries/usePlaygroundMode';
import { useState } from 'react';

/**
 * QA Playground "Reset demo data" (Feature #8595). Rendered in the profile menu just below
 * PlaygroundModeMenuToggle, and only while Playground Mode is ON for this session.
 *
 * #9141: there is no "Load Demo Data" button any more. The design prototype shows demo data
 * the moment Playground is turned ON, so the toggle seeds it automatically. Reset stays (user
 * decision, 2026-10-20) so a demo that was clicked through can be restored before the next
 * pitch. It is destructive (wipes the demo rows before re-seeding), so it sits behind the same
 * confirm-dialog pattern as ReplayOnboardedSupplierLinksCard.
 */
const PlaygroundDemoDataActions = () => {
  const { isActive, isUnavailable } = usePlaygroundMode();
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const reset = useResetPlaygroundDemoDataMutation();

  if (!isActive || isUnavailable) {
    return null;
  }

  const handleConfirmReset = () => {
    reset.mutate(undefined, { onSettled: () => setResetConfirmOpen(false) });
  };

  return (
    <div className="px-4 pb-2.5" onPointerDown={(e) => e.stopPropagation()}>
      <Button
        type="button"
        size="sm"
        variant="outlined"
        isLoading={reset.isPending}
        onClick={() => setResetConfirmOpen(true)}
      >
        Reset demo data
      </Button>

      <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset demo data?</DialogTitle>
          </DialogHeader>
          <Typography variant="body2" className="text-muted-foreground">
            Wipes the existing demo onboarding/BBBEE rating/COI screening and re-seeds
            them fresh. Only ever affects the fixed, fabricated demo supplier - never real
            data.
          </Typography>
          <DialogFooter>
            <DialogSecondaryAction
              type="button"
              variant="outlined"
              onClick={() => setResetConfirmOpen(false)}
              disabled={reset.isPending}
            >
              Cancel
            </DialogSecondaryAction>
            <DialogPrimaryAction
              type="button"
              onClick={handleConfirmReset}
              isLoading={reset.isPending}
            >
              Reset
            </DialogPrimaryAction>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlaygroundDemoDataActions;
